import React, { useMemo, useState } from 'react';

export default function CampaignCalendar() {
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const currentYear = new Date().getFullYear();
  const today = new Date();

  const tags = ['Retail','Seasonal','Public Holiday','Bonus','Religious','Sports'];
  const [activeTags, setActiveTags] = useState(tags);
  const [selectedYear, setSelectedYear] = useState(currentYear); // allow switching 2025/2026
  const [insightsMonthIdx, setInsightsMonthIdx] = useState(null);

  const closeInsights = () => setInsightsMonthIdx(null);

  const monthLabel = (idx) => `${monthNames[idx]} ${selectedYear}`;

  // Deterministic pseudo-random helper (so dummy data is stable per month/year)
  const seededRand = (seed) => {
    let s = (seed >>> 0) || 1;
    return () => {
      // LCG
      s = (1664525 * s + 1013904223) >>> 0;
      return s / 4294967296;
    };
  };

  const getMonthlyInsightsDummy = (year, monthIdx) => {
    const r = seededRand(year * 100 + (monthIdx + 1));
    const pick = (arr, n) => {
      const a = [...arr];
      const out = [];
      while (a.length && out.length < n) {
        const i = Math.floor(r() * a.length);
        out.push(a.splice(i, 1)[0]);
      }
      return out;
    };

    const categoriesPool = [
      'Apparel & Accessories > Shoes',
      'Apparel & Accessories > Outerwear',
      'Electronics > Mobile Phones',
      'Electronics > Accessories',
      'Home & Garden > Kitchen & Dining',
      'Home & Garden > Bedding',
      'Beauty & Personal Care',
      'Sports & Outdoors',
      'Toys & Games',
      'Office Supplies',
      'Pet Supplies',
      'Baby & Toddler',
    ];
    const keywordPool = [
      'indirim',
      'black friday',
      'yılbaşı hediyesi',
      'sevgililer günü',
      'anneler günü',
      'okula dönüş',
      'spor ayakkabı',
      'kışlık mont',
      'telefon kılıfı',
      'robot süpürge',
      'kedi maması',
      'nevresim takımı',
      'klima',
      'güneş gözlüğü',
      'tatile hazırlık',
      'ramazan',
      'bayram kampanyası',
    ];
    const campaignAnglesPool = [
      'Bundle (2 al 1 öde)',
      'Sepette %X indirim',
      'Ücretsiz kargo eşiği',
      'Yeni sezon lansmanı',
      'Stok eritme / clearance',
      'Fiyat karşılaştırma mesajı',
      'Hediye rehberi / Gift guide',
    ];

    const topCats = pick(categoriesPool, 3).map((c, i) => ({
      category: c,
      expectedLiftPct: Math.round(8 + r() * 22), // 8%..30%
      confidence: ['Low', 'Medium', 'High'][Math.min(2, Math.floor(r() * 3))],
      note: i === 0 ? 'Based on last-year seasonality pattern (demo).' : 'Demo estimate.'
    }));

    const rising = pick(keywordPool, 5).map((k) => ({
      keyword: k,
      trend: ['↑', '↑↑', '→'][Math.floor(r() * 3)],
      window: ['7d', '30d'][Math.floor(r() * 2)],
      note: 'Demo trend signal'
    }));

    const angles = pick(campaignAnglesPool, 3).map((a) => ({
      angle: a,
      why: 'Demo suggestion',
    }));

    return {
      monthIdx,
      year,
      dataSource: 'demo',
      topCategories: topCats,
      risingKeywords: rising,
      suggestedCampaigns: angles,
      disclaimer: 'Demo insights: real version will use your sales history + search volume signals.'
    };
  };

  // Helpers for movable dates
  const nthWeekdayOfMonth = (year, monthIndex, weekday, n) => {
    const first = new Date(year, monthIndex, 1);
    const add = (7 + weekday - first.getDay()) % 7;
    return new Date(year, monthIndex, 1 + add + 7*(n-1));
  };
  const easterSundayWestern = (Y) => { // Anonymous Gregorian algorithm
    const a = Y % 19; const b = Math.floor(Y/100); const c = Y % 100; const d = Math.floor(b/4); const e = b % 4;
    const f = Math.floor((b+8)/25); const g = Math.floor((b-f+1)/3); const h = (19*a + b - d - g + 15) % 30;
    const i = Math.floor(c/4); const k = c % 4; const l = (32 + 2*e + 2*i - h - k) % 7;
    const m = Math.floor((a + 11*h + 22*l) / 451); const month = Math.floor((h + l - 7*m + 114) / 31) - 1; // 0=Jan
    const day = ((h + l - 7*m + 114) % 31) + 1; return new Date(Y, month, day);
  };
  const thanksgivingUS = (Y) => nthWeekdayOfMonth(Y, 10, 4, 4); // 4th Thursday of Nov (weekday: 4)
  const blackFriday = (Y) => { const t = thanksgivingUS(Y); return new Date(Y, 10, t.getDate()+1); };
  const cyberMonday = (Y) => { const b = blackFriday(Y); return new Date(Y, 10, b.getDate()+3); };
  const mothersDayTR = (Y) => nthWeekdayOfMonth(Y, 4, 0, 2); // 2nd Sunday May
  const fathersDayTR = (Y) => nthWeekdayOfMonth(Y, 5, 0, 3); // 3rd Sunday June

  // Known Islamic holiday starts (approx) for a few years (TR)
  const eidAlFitrStart = { 2025: '2025-03-31', 2026: '2026-03-21', 2027: '2027-03-10' }[selectedYear];
  const eidAlAdhaStart = { 2025: '2025-06-06', 2026: '2026-05-27', 2027: '2027-05-16' }[selectedYear];

  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  // Sömestr tatili tarihleri (yaklaşık - MEB'e göre değişebilir)
  const sompitrStart = { 2025: '2025-01-20', 2026: '2026-01-19', 2027: '2027-01-18' }[selectedYear];
  const sompitrEnd = { 2025: '2025-02-03', 2026: '2026-02-02', 2027: '2027-02-01' }[selectedYear];
  const schoolYearEnd = { 2025: '2025-06-13', 2026: '2026-06-12', 2027: '2027-06-11' }[selectedYear];
  const schoolYearStart = { 2025: '2025-09-08', 2026: '2026-09-07', 2027: '2027-09-06' }[selectedYear];

  const baseEvents = useMemo(() => {
    const Y = selectedYear;
    const defaults = [
      { date: `${Y}-01-01`, name: "New Year's Day", tags: ['Public Holiday'] },
      // Semester Break (Mid-year school holiday)
      ...(sompitrStart ? [{ date: sompitrStart, name: 'Semester Break Start (TR)', tags: ['Seasonal','Retail'] }] : []),
      ...(sompitrEnd ? [{ date: sompitrEnd, name: 'Semester Break End — Schools Reopen (TR)', tags: ['Seasonal'] }] : []),
      { date: `${Y}-02-14`, name: "Valentine's Day", tags: ['Retail','Seasonal'] },
      { date: `${Y}-03-08`, name: "International Women's Day", tags: ['Retail','Seasonal'] },
      { date: `${Y}-03-15`, name: 'World Consumer Rights Day', tags: ['Retail'] },
      // Astronomical milestones (approx; varies by year/timezone)
      { date: `${Y}-03-20`, name: 'Spring Equinox (approx.)', tags: ['Seasonal'] },
      { date: `${Y}-06-21`, name: 'Summer Solstice — Longest Day (N. Hemisphere, approx.)', tags: ['Seasonal'] },
      { date: `${Y}-09-22`, name: 'Autumn Equinox (approx.)', tags: ['Seasonal'] },
      ...(eidAlFitrStart ? [{ date: eidAlFitrStart, name: 'Ramadan Feast (Eid al-Fitr) — Start', tags: ['Public Holiday','Religious'] }] : []),
      { date: `${Y}-04-22`, name: 'Earth Day', tags: ['Seasonal'] },
      { date: `${Y}-04-23`, name: "National Sovereignty & Children's Day (TR)", tags: ['Public Holiday','Retail'] },
      { date: toISO(easterSundayWestern(Y)), name: 'Easter Sunday', tags: ['Religious','Retail'] },
      { date: `${Y}-05-01`, name: 'Labour Day (TR)', tags: ['Public Holiday'] },
      { date: toISO(mothersDayTR(Y)), name: "Mother's Day (TR)", tags: ['Retail','Seasonal'] },
      { date: `${Y}-05-19`, name: 'Youth & Sports Day (TR)', tags: ['Public Holiday','Retail'] },
      { date: toISO(fathersDayTR(Y)), name: "Father's Day (TR)", tags: ['Retail','Seasonal'] },
      ...(eidAlAdhaStart ? [{ date: eidAlAdhaStart, name: 'Sacrifice Feast (Eid al-Adha) — Start', tags: ['Public Holiday','Religious'] }] : []),
      // Summer Holiday
      ...(schoolYearEnd ? [{ date: schoolYearEnd, name: 'Summer Holiday Start — Schools Close (TR)', tags: ['Seasonal','Retail'] }] : []),
      { date: `${Y}-07-15`, name: 'Democracy & National Unity Day (TR)', tags: ['Public Holiday'] },
      { date: `${Y}-08-30`, name: 'Victory Day (TR)', tags: ['Public Holiday'] },
      // Back to School Season
      ...(schoolYearStart ? [{ date: schoolYearStart, name: 'Back to School — Schools Reopen (TR)', tags: ['Seasonal','Retail'] }] : []),
      { date: `${Y}-10-04`, name: 'World Animal Day', tags: ['Seasonal'] },
      { date: `${Y}-10-29`, name: 'Republic Day (TR)', tags: ['Public Holiday'] },
      { date: `${Y}-10-31`, name: 'Halloween', tags: ['Seasonal','Retail'] },
      { date: `${Y}-11-10`, name: 'Ataturk Commemoration Day (TR)', tags: ['Public Holiday'] },
      { date: `${Y}-11-11`, name: 'Singles Day (11.11)', tags: ['Retail'] },
      { date: `${Y}-11-24`, name: "Teachers' Day (TR)", tags: ['Seasonal'] },
      { date: toISO(blackFriday(Y)), name: 'Black Friday', tags: ['Retail'] },
      { date: toISO(cyberMonday(Y)), name: 'Cyber Monday', tags: ['Retail'] },
      { date: `${Y}-12-25`, name: 'Christmas Day', tags: ['Public Holiday','Religious','Retail'] },
      { date: `${Y}-12-21`, name: 'Winter Solstice — Longest Night (N. Hemisphere, approx.)', tags: ['Seasonal'] },
      { date: `${Y}-12-31`, name: "New Year's Eve", tags: ['Retail','Seasonal'] },
    ];
    // 2026 FIFA World Cup details (North America): Opening, key rounds and Final
    if (Y === 2026) {
      defaults.push(
        // UEFA Champions League 2025/26 season milestones (approx.)
        { date: '2026-02-17', name: 'UEFA Champions League — Knockout Stage (est. start)', tags: ['Sports'] },
        { date: '2026-03-10', name: 'UEFA Champions League — Round of 16 (est. window)', tags: ['Sports'] },
        { date: '2026-04-07', name: 'UEFA Champions League — Quarter-finals (est. window)', tags: ['Sports'] },
        { date: '2026-04-28', name: 'UEFA Champions League — Semi-finals (est. window)', tags: ['Sports'] },
        { date: '2026-05-30', name: 'UEFA Champions League Final (est.)', tags: ['Sports','Retail'] },
        { date: '2026-06-11', name: 'FIFA World Cup — Opening Match', tags: ['Sports','Retail'] },
        { date: '2026-06-12', name: 'FIFA World Cup — Group Stage (window)', tags: ['Sports'] },
        { date: '2026-07-04', name: 'FIFA World Cup — Round of 16 (start, est.)', tags: ['Sports'] },
        { date: '2026-07-11', name: 'FIFA World Cup — Semi-finals (est.)', tags: ['Sports'] },
        { date: '2026-07-19', name: 'FIFA World Cup — Final (est.)', tags: ['Sports','Retail'] },
        // UEFA Champions League 2026/27 season kickoff (approx.)
        { date: '2026-08-27', name: 'UEFA Champions League — Group Stage Draw (est.)', tags: ['Sports'] },
        { date: '2026-09-15', name: 'UEFA Champions League — Group Stage (est. start)', tags: ['Sports','Retail'] },
        // Extra planning hooks (approx.)
        { date: '2026-06-30', name: 'Mid‑Year Clearance / Summer Sale Kickoff (est.)', tags: ['Retail','Seasonal'] },
        { date: '2026-11-01', name: 'Winter Prep / Early Gifting (est.)', tags: ['Retail','Seasonal'] },
      );
    }
    return defaults;
  }, [selectedYear, eidAlFitrStart, eidAlAdhaStart, sompitrStart, sompitrEnd, schoolYearEnd, schoolYearStart]);

  const eventsByMonth = useMemo(() => {
    const byM = Array.from({ length: 12 }, () => []);
    baseEvents.forEach(ev => { const m = new Date(ev.date).getMonth(); byM[m].push(ev); });
    byM.forEach(arr => arr.sort((a,b) => new Date(a.date) - new Date(b.date)));
    return byM;
  }, [baseEvents]);

  const isUpcoming = (dateStr) => {
    const d = new Date(dateStr);
    const diff = (d - today) / (1000*60*60*24);
    return diff >= 0 && diff <= 60;
  };

  const formatWithWeekday = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const tagStyle = (t, active) => {
    const base = 'px-3 py-1.5 rounded-full text-xs border transition font-medium';
    const inactive = 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50';
    const styles = {
      Retail: active ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : inactive,
      Seasonal: active ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm' : inactive,
      'Public Holiday': active ? 'bg-rose-600 text-white border-rose-600 shadow-sm' : inactive,
      Bonus: active ? 'bg-amber-500 text-white border-amber-500 shadow-sm' : inactive,
      Religious: active ? 'bg-violet-600 text-white border-violet-600 shadow-sm' : inactive,
      Sports: active ? 'bg-sky-600 text-white border-sky-600 shadow-sm' : inactive,
    };
    return `${base} ${styles[t] || (active ? 'bg-gray-900 text-white border-gray-900 shadow-sm' : inactive)}`;
  };

  const chipStyle = (t) => {
    const base = 'px-2 py-0.5 rounded-md text-[11px] border';
    const map = {
      Retail: `${base} bg-indigo-50 text-indigo-700 border-indigo-200`,
      Seasonal: `${base} bg-emerald-50 text-emerald-700 border-emerald-200`,
      'Public Holiday': `${base} bg-rose-50 text-rose-700 border-rose-200`,
      Bonus: `${base} bg-amber-50 text-amber-800 border-amber-200`,
      Religious: `${base} bg-violet-50 text-violet-700 border-violet-200`,
      Sports: `${base} bg-sky-50 text-sky-700 border-sky-200`,
    };
    return map[t] || `${base} bg-white text-gray-700 border-gray-200`;
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="rounded-2xl overflow-hidden border shadow-sm">
          <div className="p-6 bg-gradient-to-r from-indigo-50 via-white to-emerald-50 text-gray-900 border-b flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Marketing Holiday Planner</h1>
              <p className="text-sm text-gray-500">Year-round retail, seasonal and sports opportunities with upcoming highlights</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="border rounded-lg px-3 py-1.5 text-sm bg-white/80 backdrop-blur focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
              >
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
              </select>
            </div>
          </div>

          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center gap-2 flex-wrap">
              {tags.map(t => (
                <button
                  key={t}
                  className={tagStyle(t, activeTags.includes(t))}
                  onClick={() => setActiveTags(prev => prev.includes(t) ? prev.filter(x=>x!==t) : [...prev, t])}
                >{t}</button>
              ))}
            </div>
          </div>

          <div className="px-6 py-4 bg-white">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {monthNames.map((name, idx) => (
                <div key={idx} className="rounded-xl border p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-gray-900">{name} {selectedYear}</div>
                    <button
                      className="text-[11px] px-2.5 py-1 rounded-lg border border-indigo-200 bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 flex items-center gap-1"
                      onClick={() => setInsightsMonthIdx(idx)}
                      title="Monthly insights (demo)"
                    >
                      <span aria-hidden>✨</span>
                      Insights
                    </button>
                  </div>
                  <div className="space-y-2">
                    {eventsByMonth[idx]
                      .filter(ev => ev.tags.some(t => activeTags.includes(t)))
                      .map((ev, i) => (
                      <div key={i} className={`p-3 rounded-lg border ${isUpcoming(ev.date) ? 'bg-gradient-to-r from-amber-50 to-white border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-900 truncate">{ev.name}</div>
                          <div className="text-xs text-gray-500 whitespace-nowrap">{formatWithWeekday(ev.date)}</div>
                        </div>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          {ev.tags.map(t => (
                            <span key={t} className={chipStyle(t)}>{t}</span>
                          ))}
                          {isUpcoming(ev.date) && (
                            <span className="px-2 py-0.5 rounded-md text-[11px] bg-amber-100 text-amber-900 border border-amber-200 font-medium">Upcoming</span>
                          )}
                        </div>
                      </div>
                    ))}
                    {eventsByMonth[idx].filter(ev => ev.tags.some(t => activeTags.includes(t))).length === 0 && (
                      <div className="text-xs text-gray-400">No events</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Insights Modal (demo) */}
      {insightsMonthIdx != null && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={closeInsights}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border w-full max-w-3xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const data = getMonthlyInsightsDummy(selectedYear, insightsMonthIdx);
              return (
                <>
                  <div className="p-5 border-b flex items-start justify-between gap-4">
                    <div>
                      <div className="text-lg font-semibold text-gray-900">{monthLabel(insightsMonthIdx)} — Monthly Insights</div>
                      <div className="text-xs text-gray-500">{data.disclaimer}</div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600" onClick={closeInsights}>✕</button>
                  </div>

                  <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-xl border p-4">
                      <div className="text-sm font-medium text-gray-900 mb-2">Top categories (expected)</div>
                      <div className="space-y-2">
                        {data.topCategories.map((c, i) => (
                          <div key={i} className="p-3 rounded-lg bg-gray-50 border">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-xs font-medium text-gray-900">{c.category}</div>
                              <div className="text-xs text-gray-700 whitespace-nowrap">+{c.expectedLiftPct}%</div>
                            </div>
                            <div className="mt-1 flex items-center justify-between text-[11px] text-gray-600">
                              <span>Confidence: {c.confidence}</span>
                              <span>{c.note}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-xl border p-4">
                      <div className="text-sm font-medium text-gray-900 mb-2">Rising keywords</div>
                      <div className="space-y-2">
                        {data.risingKeywords.map((k, i) => (
                          <div key={i} className="p-3 rounded-lg bg-gray-50 border">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-xs font-medium text-gray-900">{k.keyword}</div>
                              <div className="text-xs text-gray-700 whitespace-nowrap">{k.trend} ({k.window})</div>
                            </div>
                            <div className="mt-1 text-[11px] text-gray-600">{k.note}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-xl border p-4 md:col-span-2">
                      <div className="text-sm font-medium text-gray-900 mb-2">Suggested campaign angles</div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {data.suggestedCampaigns.map((a, i) => (
                          <div key={i} className="p-3 rounded-lg bg-gray-50 border">
                            <div className="text-xs font-medium text-gray-900">{a.angle}</div>
                            <div className="mt-1 text-[11px] text-gray-600">{a.why}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 text-[11px] text-gray-500">
                        Next step: plug in real signals (last-year sales by category + onsite/search volume) and show confidence + recommended budget split.
                      </div>
                    </div>
                  </div>

                  <div className="p-4 border-t bg-gray-50 flex items-center justify-end gap-2">
                    <button className="px-3 py-2 text-xs font-medium rounded-lg border bg-white hover:bg-gray-50" onClick={closeInsights}>Close</button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}


